import React, { useState } from "react";
import { Field, ErrorMessage } from "formik";
import { get } from "lodash";
import {
  UploadCloud,
  X,
  GripVertical,
  Plus,
  AlertCircle,
  Loader2,
  Image as ImageIcon,
  Eye,
} from "lucide-react";
import { IMAGE_URL } from "../../config";
import { formatUnderscoreToSpace } from "../../utils/text-format";

export const FormMultiImageField = ({
  name = "media",
  label = "Image Upload",
  className = "",
  maxSizeMB = 100,
  ...props
}) => {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [uploadStatuses, setUploadStatuses] = useState([]);
  const [isDragOverDropZone, setIsDragOverDropZone] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const handleFileChange = async (filesArray, setFieldValue, values) => {
    const files = Array.from(filesArray || []);
    if (files.length === 0) return;

    const MAX_SIZE = maxSizeMB * 1024 * 1024;
    const validFiles = files.filter(
      (file) => file.size <= MAX_SIZE && file.type.startsWith("image/"),
    );

    if (validFiles.length === 0) return;

    const newStatuses = validFiles.map((file, idx) => ({
      id: `${Date.now()}-${idx}`,
      file,
      status: "loading",
      progress: 0,
      url: null,
    }));

    setUploadStatuses((prev) => [...prev, ...newStatuses]);

    const uploadPromises = newStatuses.map(
      (status) =>
        new Promise((resolve) => {
          const formData = new FormData();
          formData.append("file", status.file);

          const xhr = new XMLHttpRequest();

          xhr.upload.addEventListener("progress", (e) => {
            if (e.lengthComputable) {
              const percentComplete = Math.round((e.loaded / e.total) * 100);
              setUploadStatuses((prev) =>
                prev.map((s) =>
                  s.id === status.id ? { ...s, progress: percentComplete } : s,
                ),
              );
            }
          });

          xhr.onreadystatechange = () => {
            if (xhr.readyState === 4) {
              if (xhr.status >= 200 && xhr.status < 300) {
                try {
                  const response = JSON.parse(xhr.responseText);
                  const url = response.data?.filePath || response.filePath;

                  setUploadStatuses((prev) =>
                    prev.map((s) =>
                      s.id === status.id
                        ? { ...s, progress: 100, status: "done", url }
                        : s,
                    ),
                  );
                  resolve(url);
                } catch {
                  setUploadStatuses((prev) =>
                    prev.map((s) =>
                      s.id === status.id ? { ...s, status: "error" } : s,
                    ),
                  );
                  resolve(null);
                }
              } else {
                setUploadStatuses((prev) =>
                  prev.map((s) =>
                    s.id === status.id ? { ...s, status: "error" } : s,
                  ),
                );
                resolve(null);
              }
            }
          };

          xhr.open("POST", IMAGE_URL);
          xhr.send(formData);
        }),
    );

    const uploadedUrls = await Promise.all(uploadPromises);
    const successfulUrls = uploadedUrls.filter(Boolean);

    const currentMedia = get(values, name) || [];
    const updatedMedia = [...currentMedia, ...successfulUrls];

    setFieldValue(name, updatedMedia);

    // Remove completed items from status tracking
    setUploadStatuses((prev) => prev.filter((s) => s.status !== "done"));
  };

  const handleDelete = (index, setFieldValue, values) => {
    const currentMedia = get(values, name) || [];
    const newMedia = [...currentMedia];
    newMedia.splice(index, 1);
    setFieldValue(name, newMedia);
  };

  const handleDragStart = (index) => setDraggedIndex(index);
  const handleDragOverItem = (e) => e.preventDefault();

  const handleDropItem = (index, values, setFieldValue) => {
    if (draggedIndex === null || draggedIndex === index) return;
    const currentMedia = get(values, name) || [];
    const updatedItems = [...currentMedia];
    const draggedItem = updatedItems.splice(draggedIndex, 1)[0];
    updatedItems.splice(index, 0, draggedItem);
    setFieldValue(name, updatedItems);
    setDraggedIndex(null);
  };

  // Dropzone drag-and-drop support for file inputs
  const handleDropzoneDrop = (e, setFieldValue, values) => {
    e.preventDefault();
    setIsDragOverDropZone(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileChange(e.dataTransfer.files, setFieldValue, values);
    }
  };

  return (
    <Field name={name}>
      {({ form, meta }) => {
        const values = form.values;
        const mediaItems = get(values, name) || [];
        const hasError = Boolean(meta.touched && meta.error);
        const activeUploads = uploadStatuses.filter((s) => s.status !== "done");

        return (
          <div className={`w-full ${className}`}>
            {/* Header / Actions */}
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="text-sm font-semibold text-gray-800">
                  {formatUnderscoreToSpace(label)}
                </label>
                {mediaItems.length > 0 && (
                  <p className="text-xs text-gray-500">
                    {mediaItems.length}{" "}
                    {mediaItems.length === 1 ? "image" : "images"} uploaded ·
                    Drag images to reorder
                  </p>
                )}
              </div>

              {mediaItems.length > 0 && (
                <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-primary hover:bg-indigo-700 active:bg-indigo-800 rounded-lg cursor-pointer transition shadow-sm">
                  <Plus className="w-4 h-4" />
                  Add <span className="hidden md:block">Images</span>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    className="hidden"
                    onChange={(e) =>
                      handleFileChange(
                        e.target.files,
                        form.setFieldValue,
                        values,
                      )
                    }
                  />
                </label>
              )}
            </div>

            {/* Main Container */}
            <div
              className={`relative rounded-xl transition-colors ${
                hasError
                  ? "border-red-300 bg-red-50/20"
                  : "border-gray-200 bg-gray-50/50"
              }`}
            >
              {/* Image Grid & Uploading Slots */}
              {(mediaItems.length > 0 || activeUploads.length > 0) && (
                <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {/* Uploaded Media Items */}
                  {mediaItems.map((url, index) => (
                    <div
                      key={index}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={handleDragOverItem}
                      onDrop={() =>
                        handleDropItem(index, values, form.setFieldValue)
                      }
                      className="group relative aspect-square rounded-xl overflow-hidden border border-gray-200 bg-white shadow-xs transition hover:shadow-md cursor-grab active:cursor-grabbing"
                    >
                      <img
                        src={url}
                        alt={`media-${index}`}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                      />

                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2">
                        <div className="flex justify-between items-center text-white/80">
                          <GripVertical className="w-4 h-4" />
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setPreviewImage(url);
                              }}
                              className="p-1.5 rounded-full bg-white/20 hover:bg-white/40 text-white transition backdrop-blur-xs"
                              title="Preview image"
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(index, form.setFieldValue, values);
                              }}
                              className="p-1.5 rounded-full bg-white/20 hover:bg-red-600 text-white transition backdrop-blur-xs"
                              title="Remove image"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Active Uploading Status Cards */}
                  {activeUploads.map((status) => (
                    <div
                      key={status.id}
                      className="relative aspect-square rounded-xl overflow-hidden border border-gray-200 bg-white p-3 flex flex-col items-center justify-center shadow-xs"
                    >
                      {status.status === "loading" ? (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                          <div className="relative flex items-center justify-center">
                            <Loader2 className="w-7 h-7 animate-spin text-primary" />
                            <span className="absolute text-[10px] font-bold text-gray-700">
                              {status.progress}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                            <div
                              className="bg-primary h-full transition-all duration-200"
                              style={{ width: `${status.progress}%` }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-center p-2 text-red-500">
                          <AlertCircle className="w-6 h-6 mb-1" />
                          <span className="text-xs font-semibold">
                            Upload failed
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              setUploadStatuses((prev) =>
                                prev.filter((s) => s.id !== status.id),
                              )
                            }
                            className="mt-2 text-xs font-medium text-gray-500 hover:text-gray-700 underline"
                          >
                            Dismiss
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Empty State Dropzone */}
              {mediaItems.length === 0 && activeUploads.length === 0 && (
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragOverDropZone(true);
                  }}
                  onDragLeave={() => setIsDragOverDropZone(false)}
                  onDrop={(e) =>
                    handleDropzoneDrop(e, form.setFieldValue, values)
                  }
                  className={`p-8 sm:p-12 text-center rounded-xl border-2 border-dashed transition-all ${
                    isDragOverDropZone
                      ? "border-indigo-500 bg-indigo-50/50"
                      : "border-gray-200 bg-gray-50/50 hover:border-gray-400 hover:bg-gray-100/50"
                  } ${hasError ? "border-red-300 bg-red-50/20" : ""}`}
                >
                  <div className="mx-auto w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center text-primary mb-3">
                    <UploadCloud className="w-6 h-6" />
                  </div>
                  <p className="text-sm font-semibold text-gray-700">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-gray-500 mt-1 mb-4">
                    PNG, JPG, WebP up to {maxSizeMB}MB
                  </p>
                  <label className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-primary bg-indigo-50 hover:bg-indigo-100 rounded-lg cursor-pointer transition">
                    <ImageIcon className="w-4 h-4" />
                    Browse Files
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={(e) =>
                        handleFileChange(
                          e.target.files,
                          form.setFieldValue,
                          values,
                        )
                      }
                    />
                  </label>
                </div>
              )}
            </div>

            {/* Image Preview Modal */}
            {previewImage && (
              <div
                className="fixed inset-0 z-[50001] flex items-center justify-center bg-black/80 p-4 backdrop-blur-xs"
                onClick={() => setPreviewImage(null)}
              >
                <div
                  className="relative max-h-[90vh] max-w-[90vw] overflow-hidden rounded-xl bg-gray-900 shadow-2xl flex items-center justify-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={() => setPreviewImage(null)}
                    className="absolute right-3 top-3 z-10 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 transition"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  <img
                    src={previewImage}
                    alt="Preview"
                    className="max-h-[85vh] max-w-full object-contain rounded-lg p-2"
                  />
                </div>
              </div>
            )}

            {/* Formik Validation Message */}
            <div className="min-h-[1.25rem] px-1 mt-1">
              <ErrorMessage name={name}>
                {(msg) => (
                  <span className="block text-xs font-medium text-red-500">
                    {msg}
                  </span>
                )}
              </ErrorMessage>
            </div>
          </div>
        );
      }}
    </Field>
  );
};
